import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import {
	AdsMeetingTypes,
	CsmMeetingTypes,
	IAccount,
	IMeetingContext,
	NormalizedStages,
	WebsiteMeetingTypes,
} from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import {
	AdsStageOptions,
	getNoteTypeDisplayLabel,
	IAdsStageOption,
	InternalMeetingNoteType,
} from '../../../../models/AdminModels';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { DateInput } from '../../../components-v2/DateInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { brandPrimary } from '../../../styles/colors';
import { baseStyleSheet, bs } from '../../../styles/styles';
import { RadioButton } from '../../RadioButton';
import { CalendarDateIcon } from '../../svgs/icons/CalendarDateIcon';
import { ClockIcon } from '../../svgs/icons/ClockIcon';
import { NotesIcon } from '../../svgs/icons/NotesIcon';
import { TargetIcon } from '../../svgs/icons/TargetIcon';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps {
	account?: IAccount;
	className?: string;
	meetingContext?: IMeetingContext;
	onContextChanged?(context: IMeetingContext): void;
	styles?: StyleDeclarationValue[];
	internalMeetingNoteType?: InternalMeetingNoteType;
}

interface IState {
	meetingContext?: IMeetingContext;
}

class _MeetingContext extends React.Component<IProps, IState> {
	public readonly state: IState = { meetingContext: this.props.meetingContext };
	public static defaultProps: Partial<IProps> = {
		internalMeetingNoteType: InternalMeetingNoteType.CSM,
	};

	private static OnboardingTagValue = 'Onboarding';
	private static MeetingLengthOptions = [15, 30, 45, 60, 90, 120];

	private static CsmMeetingTypes = Object.values(CsmMeetingTypes);
	private static WebsiteMeetingTypes = Object.values(WebsiteMeetingTypes);
	private static AdsMeetingTypes = Object.values(AdsMeetingTypes);

	public static getDerivedStateFromProps(props: IProps, state: IState) {
		const nextState: IState = {};
		if (!!props.meetingContext && props.meetingContext !== state.meetingContext) {
			nextState.meetingContext = props.meetingContext;
		}
		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	public render() {
		const { className, styles, internalMeetingNoteType: meetingType } = this.props;
		const { meetingContext } = this.state;

		const isAdsType =
			meetingType === InternalMeetingNoteType.Ads ||
			meetingType === InternalMeetingNoteType.AdsFacebook ||
			meetingType === InternalMeetingNoteType.AdsGoogleLsa ||
			meetingType === InternalMeetingNoteType.AdsGooglePpc;
		const meetingTypes =
			meetingType === InternalMeetingNoteType.Websites
				? _MeetingContext.WebsiteMeetingTypes
				: isAdsType
					? _MeetingContext.AdsMeetingTypes
					: _MeetingContext.CsmMeetingTypes;
		const recordingLabel = getNoteTypeDisplayLabel(meetingType);
		return (
			<div
				className={`${css(styleSheet.container, ...(styles || []))} rich-content-meeting-context ${className || ''}`}
			>
				{recordingLabel ? <span className={css(styleSheet.recordingTypeBadge)}>{recordingLabel}</span> : null}
				<div className={css(styleSheet.section, recordingLabel ? styleSheet.sectionAfterRecordingBadge : null)}>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
						<CalendarDateIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
						<span>Meeting type</span>
					</div>
					<div className={css(styleSheet.columns)}>
						<Combobox
							items={meetingTypes}
							value={meetingContext?.meetingType}
							onValueChange={value =>
								this.onMeetingTypeChanged(value as CsmMeetingTypes | WebsiteMeetingTypes | AdsMeetingTypes)
							}
						>
							<ComboboxInput placeholder='Search meeting type...' showClear styles={[bs.wFull]} />
							<ComboboxContent>
								<ComboboxEmpty>No meeting types found.</ComboboxEmpty>
								<ComboboxList>
									{(type: string) => (
										<ComboboxItem key={type} value={type}>
											<ComboboxItemRadioIndicator />
											{type}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
					{meetingType === InternalMeetingNoteType.CSM ? (
						<label className={css(styleSheet.onboardingCheckboxLabel)}>
							<Checkbox
								checked={!!meetingContext?.meetingTags?.find(x => x === _MeetingContext.OnboardingTagValue)}
								id='rich-content-meeting-context-onboarding-checkbox'
								onCheckedChange={this.onOnboardingCheckboxChanged}
							/>
							<span>This was part of the onboarding process</span>
						</label>
					) : null}
				</div>
				<div className={css(styleSheet.section)}>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
						<CalendarDateIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
						<span>Meeting date</span>
					</div>
					<DateInput
						value={meetingContext?.meetingDate ? meetingContext.meetingDate.slice(0, 10) : ''}
						onChange={this.onMeetingDateChanged}
					/>
				</div>
				<div className={css(styleSheet.section)}>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
						<NotesIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
						<span>Meeting Status</span>
					</div>
					<Select value={meetingContext?.meetingStatus || 'Completed'} onValueChange={this.onStatusOptionChanged}>
						<SelectTrigger styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='Completed'>Completed</SelectItem>
							<SelectItem value='No showed'>No showed</SelectItem>
							<SelectItem value='Rescheduled'>Rescheduled</SelectItem>
						</SelectContent>
					</Select>
				</div>
				{isAdsType ? (
					<div className={css(styleSheet.section)}>
						<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
							<TargetIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
							<span>Ads Stage</span>
						</div>
						<Combobox
							items={AdsStageOptions}
							value={
								AdsStageOptions.find(
									opt =>
										opt.value === (meetingContext?.adsStage ?? this.props.account?.additionalInfo?.adsStage ?? 'None')
								) ?? null
							}
							onValueChange={value => this.onAdsStageChanged(value as IAdsStageOption | null)}
							itemToStringLabel={opt => (opt as IAdsStageOption).label}
						>
							<ComboboxInput placeholder='Select ads stage...' showClear styles={[bs.wFull]} />
							<ComboboxContent>
								<ComboboxEmpty>No stages found.</ComboboxEmpty>
								<ComboboxList>
									{(option: IAdsStageOption) => (
										<ComboboxItem key={option.value} value={option}>
											<ComboboxItemRadioIndicator />
											{option.label}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
				) : null}
				{!isAdsType && meetingType !== InternalMeetingNoteType.Websites ? (
					<div className={css(styleSheet.section)}>
						<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
							<TargetIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
							<span>Account Stage</span>
						</div>
						<Combobox
							items={NormalizedStages}
							value={meetingContext?.accountStage ?? this.props.account?.additionalInfo?.stage ?? null}
							onValueChange={value => this.onAccountStageChanged(value as string | null)}
						>
							<ComboboxInput placeholder='Select account stage...' showClear styles={[bs.wFull]} />
							<ComboboxContent>
								<ComboboxEmpty>No stages found.</ComboboxEmpty>
								<ComboboxList>
									{(stage: string) => (
										<ComboboxItem key={stage} value={stage}>
											<ComboboxItemRadioIndicator />
											{stage}
										</ComboboxItem>
									)}
								</ComboboxList>
							</ComboboxContent>
						</Combobox>
					</div>
				) : null}
				<div className={css(styleSheet.section)}>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.sectionHeader)}>
						<ClockIcon fillColor={brandPrimary} className={css(styleSheet.sectionHeaderIcon)} />
						<span>Meeting Length</span>
					</div>
					<div className={css(baseStyleSheet.verticalStack)}>
						{_MeetingContext.MeetingLengthOptions.map(timeInMinutes => {
							return (
								<RadioButton
									checked={meetingContext?.durationInMinutes === timeInMinutes && !!meetingContext?.meetingType}
									className={css(styleSheet.radioOption)}
									disabled={!meetingContext?.meetingType}
									id={`meetingLengthButton-${timeInMinutes}`}
									key={`meetingLengthButton-${timeInMinutes}`}
									name='meetingLengthButton'
									onChange={this.onMeetingLengthChanged}
									value={timeInMinutes}
								>
									<span>{`${timeInMinutes} min`}</span>
								</RadioButton>
							);
						})}
					</div>
				</div>
			</div>
		);
	}

	private onOnboardingCheckboxChanged = (checked: boolean) => {
		let meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
		};
		meetingContext = this.updateOnboardingCheckbox(meetingContext, checked);

		this.updateMeetingContext(meetingContext);
	};

	private updateOnboardingCheckbox(meetingContext: IMeetingContext, checked: boolean) {
		const newMeetingContext = { ...meetingContext };
		newMeetingContext.meetingTags =
			checked &&
			(!newMeetingContext.meetingTags ||
				newMeetingContext.meetingTags.findIndex(x => x === _MeetingContext.OnboardingTagValue) < 0)
				? [...(newMeetingContext.meetingTags || []), _MeetingContext.OnboardingTagValue]
				: (newMeetingContext.meetingTags || []).filter(x => x !== _MeetingContext.OnboardingTagValue);
		return newMeetingContext;
	}

	private onMeetingTypeChanged = (meetingType: CsmMeetingTypes | WebsiteMeetingTypes | AdsMeetingTypes | null) => {
		if (!meetingType) {
			this.updateMeetingContext({
				...((this.state.meetingContext || {}) as IMeetingContext),
				meetingType: null,
			});
			return;
		}
		let meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			meetingType,
		};

		meetingContext = this.updateOnboardingCheckbox(meetingContext, meetingType === 'Kickoff');

		const getDefaultMeetingDuration = (
			type: CsmMeetingTypes | WebsiteMeetingTypes | AdsMeetingTypes,
			internalMeetingNoteType: InternalMeetingNoteType
		): number => {
			// Handle CSM meeting types
			if (internalMeetingNoteType === InternalMeetingNoteType.CSM) {
				switch (type as CsmMeetingTypes) {
					case CsmMeetingTypes.AIContentReview:
						return 30;
					case CsmMeetingTypes.CheckIn:
						return 45;
					case CsmMeetingTypes.ClientServices:
						return 30;
					case CsmMeetingTypes.ContentPlanning:
						return 45;
					case CsmMeetingTypes.CustomContent:
						return 30;
					case CsmMeetingTypes.CustomerSave:
						return 30;
					case CsmMeetingTypes.DataRefresh:
						return 30;
					case CsmMeetingTypes.IntroAndConsult:
						return 45;
					case CsmMeetingTypes.Onboarding:
						return 45;
					case CsmMeetingTypes.RenewalCall:
						return 45;
					case CsmMeetingTypes.SupportCall:
						return 30;
					case CsmMeetingTypes.TrainingStrategy:
						return 60;
					default:
						return 45;
				}
			}

			// Handle Website meeting types
			if (internalMeetingNoteType === InternalMeetingNoteType.Websites) {
				switch (type as WebsiteMeetingTypes) {
					case WebsiteMeetingTypes.GoLive:
						return 30;
					case WebsiteMeetingTypes.Kickoff:
						return 30;
					case WebsiteMeetingTypes.CheckIn:
						return 45;
					default:
						return 30;
				}
			}

			// Handle Ads meeting types (catch-all + per-channel)
			if (
				internalMeetingNoteType === InternalMeetingNoteType.Ads ||
				internalMeetingNoteType === InternalMeetingNoteType.AdsFacebook ||
				internalMeetingNoteType === InternalMeetingNoteType.AdsGoogleLsa ||
				internalMeetingNoteType === InternalMeetingNoteType.AdsGooglePpc
			) {
				switch (type as AdsMeetingTypes) {
					case AdsMeetingTypes.Kickoff:
						return 45;
					case AdsMeetingTypes.CheckIn:
						return 30;
					case AdsMeetingTypes.Live:
						return 30;
					default:
						return 30;
				}
			}

			return 45; // Default fallback
		};

		this.updateMeetingContext({
			...meetingContext,

			durationInMinutes: getDefaultMeetingDuration(meetingContext?.meetingType, this.props.internalMeetingNoteType),
		});
	};

	private onMeetingDateChanged = (value: string) => {
		const meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			meetingDate: value || undefined,
		};

		this.updateMeetingContext(meetingContext);
	};

	private onStatusOptionChanged = (value: string) => {
		const meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			meetingStatus: value,
		};

		const status = meetingContext.meetingStatus.toLocaleLowerCase();
		if (status === 'no showed' || status === 'rescheduled') {
			meetingContext.durationInMinutes = 15;
		}

		this.updateMeetingContext(meetingContext);
	};

	private onAdsStageChanged = (option: IAdsStageOption | null) => {
		const meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			adsStage: option?.value ?? '',
		};

		this.updateMeetingContext(meetingContext);
	};

	private onAccountStageChanged = (value: string | null) => {
		const meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			accountStage: value ?? '',
		};

		this.updateMeetingContext(meetingContext);
	};

	private onMeetingLengthChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.checked) {
			return;
		}
		const meetingContext: IMeetingContext = {
			...((this.state.meetingContext || {}) as IMeetingContext),
			durationInMinutes: parseInt(e.target.value, 10),
		};

		this.updateMeetingContext(meetingContext);
	};

	private updateMeetingContext = (meetingContext: IMeetingContext) => {
		const { onContextChanged } = this.props;
		if (onContextChanged) {
			onContextChanged(meetingContext);
		} else {
			this.setState({
				meetingContext,
			});
		}
	};
}

const MeetingContextAsObserver = observer(_MeetingContext);
export const MeetingContext = withEventLogging(MeetingContextAsObserver, 'MeetingContext');
